import React, { useState, useEffect, useRef } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useGoogleMaps } from './GoogleMapsContext';
import NPMapPinPicker from './NPMapPinPicker';

const MIN_PLACE_QUERY_LENGTH = 2;
const CANADIAN_POSTAL_QUERY_PATTERN = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z](?:\s?\d(?:[ABCEGHJ-NPRSTV-Z]\d?)?)?$/i;
const NUMERIC_STREET_QUERY_PATTERN = /^(?:#\s*)?\d+[a-z]*(?:[-/]\d+[a-z]*)?\s+.+\b(?:ave(?:nue)?|blvd|boulevard|cir(?:cle)?|close|court|cres(?:cent)?|dr(?:ive)?|hwy|highway|lane|ln|parkway|pkwy|place|pl|rd|road|route|st|street|terrace|trail|way)\.?\b/i;

const normalizeQueryText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

export const isLikelyPlaceQuery = (query) => {
    const rawQuery = String(query ?? '').trim();
    const normalizedQuery = normalizeQueryText(rawQuery);

    if (
        rawQuery.length < MIN_PLACE_QUERY_LENGTH
        || normalizedQuery.length < MIN_PLACE_QUERY_LENGTH
        || !/\p{L}/u.test(normalizedQuery)
    ) {
        return false;
    }

    return !CANADIAN_POSTAL_QUERY_PATTERN.test(rawQuery)
        && !NUMERIC_STREET_QUERY_PATTERN.test(rawQuery);
};

const NAMED_PLACE_TYPES = new Set(['establishment', 'point_of_interest']);
const ADDRESS_ONLY_PLACE_TYPES = new Set([
    'country',
    'floor',
    'geocode',
    'intersection',
    'locality',
    'neighborhood',
    'political',
    'post_box',
    'premise',
    'room',
    'route',
    'street_address',
    'subpremise',
]);

const normalizeAddressText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const isAddressOnlyPlaceType = (type) => (
    ADDRESS_ONLY_PLACE_TYPES.has(type)
    || /^(administrative_area|postal_code|sublocality)/.test(type)
);

export const getApplicableGooglePlaceName = (suggestion, geocodeResult) => {
    const types = [
        ...(Array.isArray(suggestion?.types) ? suggestion.types : []),
        ...(Array.isArray(geocodeResult?.types) ? geocodeResult.types : []),
    ];
    const candidate = String(
        suggestion?.formattedSuggestion?.mainText
        ?? String(suggestion?.description ?? '').split(',')[0],
    ).trim();

    if (!candidate) return '';
    if (types.some((type) => NAMED_PLACE_TYPES.has(type))) return candidate;
    if (types.length === 0 || types.every(isAddressOnlyPlaceType)) return '';

    const formattedAddress = String(geocodeResult?.formatted_address ?? '').trim();
    const normalizedCandidate = normalizeAddressText(candidate);

    if (
        !normalizedCandidate
        || normalizedCandidate === normalizeAddressText(formattedAddress)
        || normalizedCandidate === normalizeAddressText(formattedAddress.split(',')[0])
        || /^\d+[a-z]?(?:[\s-]|$)/i.test(candidate)
    ) {
        return '';
    }

    return candidate;
};

const NPPlacesInput = ({
    value = '',
    onResultSelected,
    onAddressChange,
    placeholder,
    inputId,
    inputName,
    inputClassName = 'form-control',
    placeName,
    enableMapPicker = false,
    location,
    locationType = 'pickup',
    mapPinAriaLabel,
    mapPinTestId,
}) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const [localAddress, setLocalAddress] = useState(value);
    const [isFocused, setIsFocused] = useState(false);
    const [mapPicker, setMapPicker] = useState({show: false, searchQuery: ''});
    const manualAddressRef = useRef(null);
    const locationRequestIdRef = useRef(0);
    const onResultSelectedRef = useRef(onResultSelected);
    onResultSelectedRef.current = onResultSelected;

    useEffect(() => {
        setLocalAddress(value || '');
    }, [value]);

    const handleAddressChange = (address) => {
        locationRequestIdRef.current += 1;
        manualAddressRef.current = address;
        setLocalAddress(address);
        onAddressChange?.(address);
    };

    const handleSelectionAddressChange = (address) => {
        manualAddressRef.current = null;
        setLocalAddress(address);
        onAddressChange?.(address);
    };

    const openMapPicker = (searchQuery = '') => {
        locationRequestIdRef.current += 1;
        manualAddressRef.current = null;
        setMapPicker({
            show: true,
            searchQuery: String(searchQuery ?? '').trim(),
        });
    };

    const closeMapPicker = () => {
        setMapPicker({show: false, searchQuery: ''});
    };

    useEffect(() => {
        const addressToGeocode = localAddress;
        const timeoutId = setTimeout(() => {
            if (manualAddressRef.current === addressToGeocode) {
                geocodeByAddress(addressToGeocode)
                    .then(results => getLatLng(results[0]))
                    .then(latlng => {
                        if (manualAddressRef.current === addressToGeocode) {
                            manualAddressRef.current = null;
                            onResultSelectedRef.current?.(addressToGeocode, latlng, null);
                        }
                    })
                    .catch(error => {
                        console.error('Error getting latLng', error);
                    });
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [localAddress]);

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading...</div>;

    const showSeeLocations = enableMapPicker
        && isFocused
        && isLikelyPlaceQuery(localAddress);
    const normalizedLocationType = locationType === 'dropoff' ? 'dropoff' : 'pickup';
    const locationLabel = normalizedLocationType === 'dropoff' ? 'drop-off' : 'pickup';

    return (
        <div
            className="np-places-input"
            onFocus={() => setIsFocused(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsFocused(false);
                }
            }}
        >
            {placeName && (
                <div className="small font-weight-bold mb-1" data-testid="selected-place-name">
                    {placeName}
                </div>
            )}
            <div className={enableMapPicker ? 'input-group' : undefined}>
            <PlacesAutocomplete
            value={localAddress}
            onChange={handleAddressChange}
            onSelect={(address, _placeId, suggestion) => {
                const requestId = locationRequestIdRef.current + 1;
                locationRequestIdRef.current = requestId;
                handleSelectionAddressChange(address);
                geocodeByAddress(address)
                    .then(async (results) => {
                        if (locationRequestIdRef.current !== requestId) return;
                        const geocodeResult = results[0];
                        const latlng = await getLatLng(geocodeResult);
                        if (locationRequestIdRef.current !== requestId) return;
                        const formattedAddress = geocodeResult?.formatted_address || address;
                        const placeName = getApplicableGooglePlaceName(suggestion, geocodeResult);

                        if (formattedAddress !== address) {
                            handleSelectionAddressChange(formattedAddress);
                        }
                        onResultSelectedRef.current?.(formattedAddress, latlng, placeName || null);
                    })
                    .catch(error => {
                        if (locationRequestIdRef.current === requestId) {
                            console.error('Error getting latLng', error);
                        }
                    });
            }}
            searchOptions={{ componentRestrictions: { country: "CA" } }}
        >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className={`autocomplete${enableMapPicker ? ' flex-grow-1' : ''}`}>
                    <input
                        {...getInputProps({
                            id: inputId,
                            name: inputName,
                            placeholder,
                            className: inputClassName,
                        })}
                    />
                    <div className="autocomplete-items">
                        {showSeeLocations && (
                            <button
                                type="button"
                                className="np-see-locations"
                                aria-label={`See locations for ${localAddress.trim()}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => openMapPicker(localAddress)}
                            >
                                <i className="fa fa-map-o" aria-hidden="true" />
                                <span>See locations</span>
                            </button>
                        )}
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion, index) => {
                            const style = suggestion.active
                                ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                                : { backgroundColor: '#ffffff', cursor: 'pointer' };
                            return (
                                <div
                                    {...getSuggestionItemProps(suggestion, { style })}
                                    key={index}
                                >
                                    {suggestion.description}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            </PlacesAutocomplete>
            {enableMapPicker && (
                <div className="input-group-append">
                    <button
                        type="button"
                        className="btn btn-outline-primary np-map-pin-button"
                        aria-label={mapPinAriaLabel || `Choose ${locationLabel} location on map`}
                        data-testid={mapPinTestId}
                        title={`Choose ${locationLabel} location on map`}
                        onClick={() => openMapPicker()}
                    >
                        <i className="fa fa-map-marker" aria-hidden="true" />
                    </button>
                </div>
            )}
            </div>

            {enableMapPicker && (
                <NPMapPinPicker
                    show={mapPicker.show}
                    onHide={closeMapPicker}
                    initialLocation={location}
                    locationType={normalizedLocationType}
                    searchQuery={mapPicker.searchQuery || undefined}
                    onConfirm={(address, coordinates, selectedPlaceName, isMapPin = true) => {
                        locationRequestIdRef.current += 1;
                        handleSelectionAddressChange(address);
                        onResultSelectedRef.current?.(
                            address,
                            coordinates,
                            selectedPlaceName || null,
                            isMapPin,
                        );
                        closeMapPicker();
                    }}
                />
            )}
        </div>
    );
};

export default NPPlacesInput;
